import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  ArrowDown,
  ArrowRight,
  Check,
  Users,
  BookOpen,
  Sparkles,
  Compass,
  Calendar,
  Search,
  Briefcase,
  Menu,
  X,
} from "lucide-react";
import Wordmark from "../components/Wordmark";
import { usePageSEO } from "../hooks/usePageSEO";
import { PublicConsent } from "../components/CookieConsent";
import { heroImg } from "../data/mock";
import { publicApi } from "../lib/api";

/* User-supplied Vemoye brand imagery (see /app assets) */
const IMG_TWO_MEN_TABLET = "https://customer-assets-v7afamib.emergentagent.net/job_hub-connect-59/artifacts/e8kt1kfp_2.png";
const IMG_WOMAN_ANKARA = "https://customer-assets-v7afamib.emergentagent.net/job_hub-connect-59/artifacts/5j7eo4fe_3.png";
const IMG_COLLAGE = "https://customer-assets-v7afamib.emergentagent.net/job_hub-connect-59/artifacts/sjc7gt1l_ChatGPT%20Image%20Aug%2026%2C%202026%2C%2011_55_02%20PM.png";
const IMG_WOMAN_PONYTAIL = "https://customer-assets-v7afamib.emergentagent.net/job_hub-connect-59/artifacts/a19whczy_t%20%282%29.png";
const IMG_MAN_LAPTOP = "https://customer-assets-v7afamib.emergentagent.net/job_hub-connect-59/artifacts/l29wjqny_t%20%281%29.png";

const HERO_A = IMG_WOMAN_PONYTAIL;      // hero — female professional with tablet (front-right)
const HERO_B = IMG_TWO_MEN_TABLET;      // hero — networking moment (back-left)
const CONNECT_IMG = IMG_WOMAN_ANKARA;   // Connect card — bold Nigerian professional
const LEARN_IMG = IMG_MAN_LAPTOP;       // Learn card — focused knowledge worker
const ABOUT_BG = IMG_COLLAGE;           // About Vemoye full-bleed backdrop

const ApplyBtn = ({ children = "Apply to Join", className = "", ...rest }) => (
  <Link
    to="/join"
    data-testid="prelaunch-apply-cta"
    className={`inline-flex items-center gap-2 px-6 py-3 rounded-md btn-gold text-[14px] font-medium ${className}`}
    {...rest}
  >
    {children} <ArrowUpRight size={16} />
  </Link>
);

const Eyebrow = ({ children, tone = "forest" }) => (
  <div
    className="section-eyebrow"
    style={{ color: tone === "cream" ? "rgba(255,246,226,0.65)" : "rgba(18,52,40,0.6)" }}
  >
    {children}
  </div>
);

/* -------------------------------------------------------------------- */
/* Header                                                                */
/* -------------------------------------------------------------------- */
const Header = () => {
  const [open, setOpen] = useState(false);
  return (
    <header
      className="sticky top-0 z-40 backdrop-blur"
      style={{ background: "rgba(242,232,214,0.85)", borderBottom: "1px solid rgba(18,52,40,0.08)" }}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Wordmark size={32} />
        <div className="hidden md:flex items-center gap-6 text-[13px]" style={{ color: "rgba(18,52,40,0.85)" }}>
          <a href="/#proposition" className="link-underline">The Network</a>
          <a href="/#benefits" className="link-underline">What You Get</a>
          <Link to="/webinars" className="link-underline" data-testid="prelaunch-masterclasses-link">Masterclasses</Link>
          <a href="/#commitment" className="link-underline">Commitment</a>
          <a href="/#about" className="link-underline">About Vemoye</a>
          <Link to="/login" className="link-underline" data-testid="prelaunch-signin-link">Member Sign In</Link>
          <ApplyBtn className="!py-2 !px-4 !text-[13px]" />
        </div>
        <button
          className="md:hidden p-2 rounded-md"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="prelaunch-menu-toggle"
          style={{ color: "var(--forest)" }}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      {open && (
        <div className="md:hidden border-t" style={{ borderColor: "rgba(18,52,40,0.08)", background: "var(--beige)" }}>
          <div className="px-6 py-4 flex flex-col gap-3 text-[14px]" style={{ color: "rgba(18,52,40,0.9)" }}>
            <a href="/#proposition" onClick={() => setOpen(false)}>The Network</a>
            <a href="/#benefits" onClick={() => setOpen(false)}>What You Get</a>
            <Link to="/webinars" onClick={() => setOpen(false)}>Masterclasses</Link>
            <a href="/#commitment" onClick={() => setOpen(false)}>Commitment</a>
            <a href="/#about" onClick={() => setOpen(false)}>About Vemoye</a>
            <Link to="/login" onClick={() => setOpen(false)}>Member Sign In</Link>
            <ApplyBtn className="!w-full justify-center" />
          </div>
        </div>
      )}
    </header>
  );
};

/* -------------------------------------------------------------------- */
/* Hero                                                                  */
/* -------------------------------------------------------------------- */
const DiasporaCounterPill = () => {
  const [count, setCount] = useState(null);
  useEffect(() => {
    let on = true;
    publicApi.get("/stats/diaspora")
      .then(({ data }) => { if (on) setCount(data?.approved_members_count); })
      .catch(() => {});
    return () => { on = false; };
  }, []);
  if (count === null || count === undefined) return null;
  return (
    <div
      className="mt-6 inline-flex items-center gap-3 px-4 py-2.5 rounded-full fade-up delay-3"
      style={{ background: "rgba(224,104,77,0.08)", border: "1px solid rgba(224,104,77,0.25)" }}
      data-testid="prelaunch-diaspora-counter"
    >
      <span className="hero-title text-[22px] md:text-[26px] leading-none" style={{ color: "var(--orange)" }}>
        {count.toLocaleString()}
      </span>
      <span className="text-[12px] md:text-[13px]" style={{ color: "var(--forest)" }}>
        Diaspora members &amp; growing
      </span>
    </div>
  );
};

const Hero = () => (
  <section
    className="relative overflow-hidden"
    style={{ background: "var(--beige)" }}
    data-testid="prelaunch-hero"
  >
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-14 md:pt-20 pb-16 md:pb-24 grid md:grid-cols-2 gap-10 md:gap-16 items-center">
      <div className="fade-up">
        <div className="flex flex-wrap items-center gap-2 mb-6">
          <span
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-medium"
            style={{ background: "rgba(205,163,78,0.15)", color: "var(--gold-hover)" }}
          >
            <span
              className="w-1.5 h-1.5 rounded-full"
              style={{ background: "var(--gold)", boxShadow: "0 0 0 3px rgba(205,163,78,0.25)" }}
            />
            Applications Now Open
          </span>
          <span
            className="inline-flex px-3 py-1 rounded-full text-[11px] font-medium border"
            style={{ borderColor: "rgba(18,52,40,0.2)", color: "rgba(18,52,40,0.7)" }}
          >
            Founding Cohort
          </span>
        </div>

        <div className="section-eyebrow mb-4" style={{ color: "rgba(18,52,40,0.6)" }}>
          The Vemoye Diaspora Network
        </div>
        <h1
          className="hero-title text-[46px] md:text-[68px] lg:text-[82px] leading-[1.02]"
          data-testid="prelaunch-title"
        >
          What if your <span className="font-serif-i" style={{ color: "var(--orange)" }}>experience abroad</span>{" "}
          could help <span className="hand-underline" style={{ color: "var(--gold)" }}>build something</span> back home?
        </h1>

        <p
          className="mt-8 text-[16px] md:text-[18px] leading-relaxed max-w-[560px]"
          style={{ color: "rgba(18,52,40,0.85)" }}
        >
          A curated network of Nigerian diaspora professionals using their skills, experience and expertise to support
          organisations building Nigeria&rsquo;s future.
        </p>

        <div className="mt-8 flex flex-wrap gap-3">
          <ApplyBtn />
          <a
            href="#proposition"
            data-testid="prelaunch-scroll-cta"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-md btn-outline text-[14px] font-medium"
          >
            See What You&rsquo;re Joining <ArrowDown size={16} />
          </a>
        </div>

        <p className="mt-6 text-[12px]" style={{ color: "rgba(18,52,40,0.55)" }}>
          The members platform is launching soon. Applications are open now.
        </p>

        <DiasporaCounterPill />
      </div>

      {/* Two full-size polaroid cards, both fully visible with only a corner overlap */}
      <div className="relative fade-up delay-2 min-h-[560px] md:min-h-[640px] flex items-center justify-center md:justify-end">
        <div className="relative w-full max-w-[560px] h-[520px] md:h-[600px]">
          <div
            className="absolute rounded-md overflow-hidden bg-white p-3 shadow-[0_20px_40px_-20px_rgba(18,52,40,0.35)]"
            style={{ transform: "rotate(-5deg)", width: "58%", height: "82%", left: "0", top: "8%", zIndex: 1 }}
          >
            <img
              src={HERO_B}
              alt="Nigerian diaspora professional reviewing work"
              className="w-full h-full object-cover"
              style={{ objectPosition: "center top" }}
              onError={(e) => { e.currentTarget.src = heroImg; }}
            />
          </div>
          <div
            className="absolute rounded-md overflow-hidden bg-white p-3 shadow-[0_30px_60px_-25px_rgba(18,52,40,0.35)]"
            style={{ transform: "rotate(3deg)", width: "58%", height: "82%", right: "0", bottom: "0", zIndex: 2 }}
          >
            <img
              src={HERO_A}
              alt="Nigerian diaspora professionals collaborating"
              className="w-full h-full object-cover"
              onError={(e) => { e.currentTarget.src = heroImg; }}
            />
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* -------------------------------------------------------------------- */
/* Proposition                                                           */
/* -------------------------------------------------------------------- */
const Proposition = () => (
  <section
    id="proposition"
    className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 md:py-28"
  >
    <div className="grid md:grid-cols-12 gap-10">
      <div className="md:col-span-4">
        <Eyebrow>01 · The Proposition</Eyebrow>
      </div>
      <div className="md:col-span-8">
        <h2 className="classic-title text-[40px] md:text-[60px] leading-[1.02] max-w-[850px]">
          You don&rsquo;t have to move home to{" "}
          <span className="font-serif-i" style={{ color: "var(--orange)" }}>contribute</span>.
        </h2>
        <div className="mt-8 grid md:grid-cols-2 gap-8 text-[16px] leading-relaxed max-w-[880px]" style={{ color: "rgba(18,52,40,0.85)" }}>
          <p>
            Nigerian professionals across the diaspora have accumulated skills, experience, networks and expertise
            that can be genuinely valuable to organisations doing important work back home.
          </p>
          <p>
            The Vemoye Diaspora Network creates a practical structure through which people can contribute some of
            that expertise &mdash; without leaving the career they&rsquo;ve built.
          </p>
        </div>
        <div className="mt-10 flex flex-wrap gap-3 text-[12px]">
          {["Professional expertise", "Practical contribution", "Nigeria"].map((t) => (
            <span
              key={t}
              className="inline-flex px-4 py-2 rounded-full font-medium"
              style={{ background: "var(--cream)", color: "var(--forest)", border: "1px solid rgba(18,52,40,0.1)" }}
            >
              {t}
            </span>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* -------------------------------------------------------------------- */
/* Benefits — What You Get                                               */
/* -------------------------------------------------------------------- */
const Benefits = () => {
  const items = [
    {
      Icon: Users,
      title: "Connect",
      img: CONNECT_IMG,
      objectPosition: "center 20%", // keep the woman's face in frame
      bullets: [
        "A curated community of Nigerian diaspora professionals across industries and expertise.",
        "Discover other members through the Member Directory.",
        "Meet people who are thinking seriously about Nigeria and want practical ways to contribute.",
      ],
    },
    {
      Icon: BookOpen,
      title: "Learn",
      img: LEARN_IMG,
      objectPosition: "center 30%",
      bullets: [
        "Access curated masterclasses, conversations, network events and specialist sessions.",
        "Hear from people building, leading and solving problems in and around Nigeria.",
        "Reserve places at member-only events as the platform launches.",
      ],
    },
  ];

  return (
    <section
      id="benefits"
      style={{ background: "var(--paper)" }}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 md:py-28">
        <div className="grid md:grid-cols-12 gap-10 mb-14">
          <div className="md:col-span-4">
            <Eyebrow>02 · What You Get</Eyebrow>
          </div>
          <div className="md:col-span-8">
            <h2 className="classic-title text-[40px] md:text-[60px] leading-[1.02] max-w-[900px]">
              What you get by <span className="font-serif-i" style={{ color: "var(--orange)" }}>joining</span>.
            </h2>
            <p className="mt-6 text-[16px] leading-relaxed max-w-[700px]" style={{ color: "rgba(18,52,40,0.8)" }}>
              Two clear ways the network is designed to be useful to you as a member.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {items.map(({ Icon, title, img, bullets, objectPosition }) => (
            <div key={title} className="editorial-card overflow-hidden flex flex-col">
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={img}
                  alt={title}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                  style={{ objectPosition: objectPosition || "center" }}
                  onError={(e) => { e.currentTarget.src = heroImg; }}
                />
              </div>
              <div className="p-8 flex-1 flex flex-col">
                <div className="flex items-center gap-3 mb-5">
                  <div
                    className="w-11 h-11 rounded-md flex items-center justify-center"
                    style={{ background: "var(--forest)", color: "var(--gold)" }}
                  >
                    <Icon size={20} />
                  </div>
                  <h3 className="text-[30px] classic-title" style={{ color: "var(--forest)" }}>
                    {title}
                  </h3>
                </div>
                <ul className="space-y-3 text-[15px] leading-relaxed" style={{ color: "rgba(18,52,40,0.82)" }}>
                  {bullets.map((b, i) => (
                    <li key={i} className="flex gap-3">
                      <Check size={18} className="mt-0.5 shrink-0" style={{ color: "var(--gold)" }} />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 pt-10 border-t" style={{ borderColor: "rgba(18,52,40,0.1)" }}>
          <p className="text-[15px] max-w-[640px]" style={{ color: "rgba(18,52,40,0.75)" }}>
            Membership is free and by application. Applications are reviewed before membership is offered.
          </p>
          <ApplyBtn />
        </div>
      </div>
    </section>
  );
};

/* -------------------------------------------------------------------- */
/* Commitment — What We Ask                                              */
/* -------------------------------------------------------------------- */
const Commitment = () => (
  <section
    id="commitment"
    style={{ background: "var(--forest)", color: "var(--cream)" }}
  >
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 md:py-28 grid md:grid-cols-12 gap-10">
      <div className="md:col-span-4">
        <Eyebrow tone="cream">03 · The Commitment</Eyebrow>
        <h2 className="classic-title text-[40px] md:text-[60px] leading-[1.02] mt-4" style={{ color: "var(--cream)" }}>
          What we ask <span className="font-serif-i" style={{ color: "var(--gold)" }}>in return</span>.
        </h2>
        <p className="mt-6 text-[15px] leading-relaxed max-w-[420px]" style={{ color: "rgba(255,246,226,0.8)" }}>
          Membership is free, but it is not passive. The network only works if members are willing to contribute
          when a suitable opportunity comes up.
        </p>
      </div>

      <div className="md:col-span-8">
        <div className="grid grid-cols-3 gap-4 md:gap-6 mb-10">
          {[
            { big: "2", unit: "hours", tail: "a week" },
            { big: "×4", unit: "weeks", tail: "focused" },
            { big: "×1", unit: "a year", tail: "if matched" },
          ].map((c) => (
            <div
              key={c.big + c.unit}
              className="rounded-md p-5 md:p-8 border"
              style={{ borderColor: "rgba(205,163,78,0.35)", background: "rgba(205,163,78,0.06)" }}
            >
              <div className="hero-title text-[52px] md:text-[80px]" style={{ color: "var(--gold)" }}>{c.big}</div>
              <div className="text-[13px] uppercase tracking-widest font-medium mt-1" style={{ color: "var(--cream)" }}>
                {c.unit}
              </div>
              <div className="text-[12px] mt-1" style={{ color: "rgba(255,246,226,0.6)" }}>{c.tail}</div>
            </div>
          ))}
        </div>

        <div className="grid md:grid-cols-2 gap-6 text-[15px] leading-relaxed">
          {[
            "This does not mean you will constantly be volunteering. Members choose the opportunities that work for them.",
            "Members are not automatically allocated to every project. Opportunities are matched to your professional expertise.",
            "The commitment is designed to make the network practically useful to the organisations Vemoye supports.",
            "Join because you genuinely want to contribute — not just because you want another professional network.",
          ].map((line, i) => (
            <div
              key={i}
              className="pt-5 border-t flex gap-3"
              style={{ borderColor: "rgba(255,246,226,0.15)", color: "rgba(255,246,226,0.88)" }}
            >
              <ArrowRight size={16} className="mt-1 shrink-0" style={{ color: "var(--gold)" }} />
              <span>{line}</span>
            </div>
          ))}
        </div>

        <div
          className="mt-10 p-6 md:p-7 rounded-md"
          style={{ background: "rgba(255,246,226,0.08)", border: "1px solid rgba(255,246,226,0.15)" }}
        >
          <p className="text-[16px] leading-relaxed" style={{ color: "var(--cream)" }}>
            <span className="font-serif-i text-[22px]" style={{ color: "var(--gold)" }}>The maths of many hands.</span>
            <br />
            One professional giving 8 hours a year is modest. 500 professionals doing the same is{" "}
            <span style={{ color: "var(--gold)" }}>4,000 hours of high-quality expertise</span> flowing into organisations
            building Nigeria&rsquo;s future workforce &mdash; every single year.
          </p>
        </div>
      </div>
    </div>
  </section>
);

/* -------------------------------------------------------------------- */
/* Who We're Looking For                                                 */
/* -------------------------------------------------------------------- */
const Audience = () => {
  const criteria = [
    "Meaningful professional experience or expertise to contribute",
    "At least 3 years experience in your field of expertise",
    "Care about Nigeria and its future",
    "Want a practical route to contribute — not just to discuss problems",
    "Willing to share some of what you have learned professionally",
    "Value a thoughtful, high-quality professional community",
    "Can make the annual commitment of 2 hours a week for 4 weeks",
  ];

  return (
    <section
      className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 md:py-28"
    >
      <div className="grid md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <Eyebrow>04 · Who We&rsquo;re Looking For</Eyebrow>
          <h2 className="classic-title text-[40px] md:text-[56px] leading-[1.02] mt-4">
            Nigerian diaspora <span className="font-serif-i" style={{ color: "var(--orange)" }}>professionals</span>.
          </h2>
          <p className="mt-6 text-[15px] leading-relaxed max-w-[420px]" style={{ color: "rgba(18,52,40,0.8)" }}>
            Membership is by application. We&rsquo;re building a founding network of thoughtful, experienced people
            &mdash; from any industry, at any career stage &mdash; who genuinely want to contribute.
          </p>
        </div>

        <div className="md:col-span-8">
          <div className="grid md:grid-cols-2 gap-x-8 gap-y-3">
            {criteria.map((c) => (
              <div
                key={c}
                className="flex items-start gap-3 py-3 border-b"
                style={{ borderColor: "rgba(18,52,40,0.08)" }}
              >
                <div
                  className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5"
                  style={{ background: "rgba(205,163,78,0.15)", color: "var(--gold-hover)" }}
                >
                  <Check size={14} />
                </div>
                <span className="text-[15px]" style={{ color: "rgba(18,52,40,0.85)" }}>{c}</span>
              </div>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap items-center gap-6">
            <ApplyBtn />
            <span className="text-[13px]" style={{ color: "rgba(18,52,40,0.6)" }}>
              Free membership &middot; by application &middot; takes ~6 minutes
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

/* -------------------------------------------------------------------- */
/* Bring the Network to Life                                             */
/* -------------------------------------------------------------------- */
const Preview = () => {
  const cards = [
    {
      Icon: Search,
      title: "Find your people",
      desc: "Search the network by skills, industry and name — and connect with other Nigerian diaspora professionals.",
      tag: "Member Directory",
    },
    {
      Icon: Calendar,
      title: "Join the conversation",
      desc: "Reserve places at curated masterclasses and member-only events. Learn from people building in and around Nigeria.",
      tag: "Masterclasses & Events",
    },
    {
      Icon: Briefcase,
      title: "Put your skills to work",
      desc: "Discover matched opportunities where an organisation needs expertise you already have.",
      tag: "Opportunities",
    },
  ];

  return (
    <section
      style={{ background: "var(--paper)" }}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 md:py-28">
        <div className="grid md:grid-cols-12 gap-10 mb-14">
          <div className="md:col-span-4">
            <Eyebrow>05 · Inside the Network</Eyebrow>
          </div>
          <div className="md:col-span-8">
            <h2 className="classic-title text-[40px] md:text-[60px] leading-[1.02] max-w-[880px]">
              A glimpse of what <span className="font-serif-i" style={{ color: "var(--orange)" }}>membership</span> will feel like.
            </h2>
            <p className="mt-6 text-[16px] leading-relaxed max-w-[700px]" style={{ color: "rgba(18,52,40,0.8)" }}>
              The member platform is launching soon. Here&rsquo;s a preview of what you&rsquo;ll be able to do inside.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {cards.map(({ Icon, title, desc, tag }) => (
            <div key={title} className="editorial-card p-8 flex flex-col h-full">
              <div className="flex items-center justify-between mb-6">
                <div
                  className="w-12 h-12 rounded-md flex items-center justify-center"
                  style={{ background: "rgba(205,163,78,0.15)", color: "var(--gold-hover)" }}
                >
                  <Icon size={22} />
                </div>
                <span
                  className="text-[11px] uppercase tracking-widest font-medium px-2.5 py-1 rounded"
                  style={{ background: "var(--beige)", color: "rgba(18,52,40,0.7)" }}
                >
                  {tag}
                </span>
              </div>
              <h3 className="classic-title text-[26px] mb-3" style={{ color: "var(--forest)" }}>{title}</h3>
              <p className="text-[15px] leading-relaxed" style={{ color: "rgba(18,52,40,0.78)" }}>{desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* -------------------------------------------------------------------- */
/* About Vemoye                                                          */
/* -------------------------------------------------------------------- */
const About = () => (
  <section id="about" className="relative overflow-hidden">
    <div className="absolute inset-0">
      <img src={ABOUT_BG} alt="" className="w-full h-full object-cover" />
      <div
        className="absolute inset-0"
        style={{ background: "linear-gradient(180deg, rgba(18,52,40,0.94), rgba(18,52,40,0.88))" }}
      />
    </div>
    <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-20 md:py-28" style={{ color: "var(--cream)" }}>
      <div className="grid md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <Eyebrow tone="cream">06 · About Vemoye Foundation</Eyebrow>
          <h2 className="classic-title text-[40px] md:text-[56px] leading-[1.02] mt-4" style={{ color: "var(--cream)" }}>
            Building Nigeria&rsquo;s <span className="font-serif-i" style={{ color: "var(--gold)" }}>future workforce</span>.
          </h2>
        </div>
        <div className="md:col-span-8">
          <div className="grid md:grid-cols-2 gap-8 text-[16px] leading-relaxed" style={{ color: "rgba(255,246,226,0.88)" }}>
            <p>
              Vemoye Foundation exists to strengthen the organisations equipping Nigerian young people with the skills,
              mindset and opportunities to solve real problems and build Nigeria&rsquo;s future.
            </p>
            <p>
              We help these organisations scale &mdash; the ones already doing meaningful work in STEM, Entrepreneurship,
              and Leadership development for Nigerian students.
            </p>
          </div>
          <div className="mt-10 flex flex-wrap gap-3">
            {["STEM", "Entrepreneurship", "Leadership"].map((t) => (
              <span
                key={t}
                className="inline-flex px-4 py-2 rounded-full text-[13px] font-medium"
                style={{ background: "rgba(205,163,78,0.18)", color: "var(--gold)", border: "1px solid rgba(205,163,78,0.35)" }}
              >
                {t}
              </span>
            ))}
          </div>
          <p className="mt-8 text-[15px] leading-relaxed max-w-[720px]" style={{ color: "rgba(255,246,226,0.75)" }}>
            The Diaspora Network is one of the ways Vemoye brings additional capacity, expertise and professional
            support to those organisations.
          </p>
        </div>
      </div>
    </div>
  </section>
);

/* -------------------------------------------------------------------- */
/* Application CTA                                                       */
/* -------------------------------------------------------------------- */
const ApplicationCTA = () => (
  <section id="apply" style={{ background: "var(--beige)" }}>
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 md:py-28 text-center">
      <Eyebrow>07 · Apply</Eyebrow>
      <h2 className="classic-title text-[42px] md:text-[68px] leading-[1.02] max-w-[1050px] mx-auto mt-6">
        You&rsquo;ve built expertise. Put some of it to work for{" "}
        <span className="font-serif-i" style={{ color: "var(--orange)" }}>Nigeria</span>.
      </h2>
      <p className="mt-8 max-w-[720px] mx-auto text-[16px] md:text-[17px] leading-relaxed" style={{ color: "rgba(18,52,40,0.85)" }}>
        Applications to the Vemoye Diaspora Network are now open. Apply now to join the founding waiting list ahead of
        the network&rsquo;s launch.
      </p>

      <div className="mt-10 flex flex-col items-center gap-4">
        <ApplyBtn className="!px-8 !py-4 !text-[15px]" />
        <p className="text-[13px]" style={{ color: "rgba(18,52,40,0.6)" }}>
          Free membership. By application. Minimum commitment: 2 hours a week for 4 weeks, once a year.
        </p>
      </div>

      <div
        className="mt-14 max-w-[820px] mx-auto p-6 md:p-7 rounded-md text-left"
        style={{ background: "var(--cream)", border: "1px solid rgba(18,52,40,0.1)" }}
      >
        <div className="section-eyebrow mb-2" style={{ color: "rgba(18,52,40,0.6)" }}>What happens next</div>
        <p className="text-[15px] leading-relaxed" style={{ color: "rgba(18,52,40,0.85)" }}>
          We&rsquo;re currently building our founding network. Applications are open now and will be{" "}
          <span style={{ color: "var(--forest)", fontWeight: 600 }}>waitlisted</span> until we begin reviewing ahead of
          the membership platform launch. Submitting now does not mean immediate membership &mdash; but it does put you
          at the front of the queue when reviews open.
        </p>
      </div>
    </div>
  </section>
);

/* -------------------------------------------------------------------- */
/* Footer                                                                */
/* -------------------------------------------------------------------- */
const Footer = () => (
  <footer style={{ background: "var(--forest)", color: "rgba(255,246,226,0.85)" }}>
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14">
      <div className="grid md:grid-cols-12 gap-10 mb-10">
        <div className="md:col-span-5">
          <Wordmark size={36} color="var(--gold)" />
          <p className="mt-4 text-[14px] leading-relaxed max-w-[380px]" style={{ color: "rgba(255,246,226,0.72)" }}>
            Vemoye Foundation strengthens the organisations equipping Nigerian young people with the skills, mindset
            and opportunity to build Nigeria&rsquo;s future.
          </p>
        </div>
        <div className="md:col-span-3">
          <div className="section-eyebrow mb-4" style={{ color: "rgba(255,246,226,0.55)" }}>Contact</div>
          <a
            href="mailto:hello@vemoyefoundation.org"
            className="link-underline text-[14px]"
            style={{ color: "var(--cream)" }}
          >
            hello@vemoyefoundation.org
          </a>
        </div>
        <div className="md:col-span-4">
          <div className="section-eyebrow mb-4" style={{ color: "rgba(255,246,226,0.55)" }}>Legal</div>
          <div className="flex flex-col gap-2 text-[14px]">
            <Link to="/privacy" className="link-underline" style={{ color: "var(--cream)" }}>Privacy Policy</Link>
            <Link to="/terms" className="link-underline" style={{ color: "var(--cream)" }}>Terms &amp; Conditions</Link>
            <span style={{ color: "rgba(255,246,226,0.55)" }}>Registered Charity No. 1188086</span>
          </div>
        </div>
      </div>
      <div
        className="pt-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-[12px] border-t"
        style={{ borderColor: "rgba(255,246,226,0.12)", color: "rgba(255,246,226,0.55)" }}
      >
        <div>&copy; {new Date().getFullYear()} Vemoye Foundation</div>
        <div>Building Nigeria&rsquo;s future workforce.</div>
      </div>
    </div>
  </footer>
);

/* -------------------------------------------------------------------- */
/* Page                                                                  */
/* -------------------------------------------------------------------- */
const PreLaunch = () => {
  usePageSEO("/");
  useEffect(() => {
    // ensure smooth in-page scroll for anchor links
    document.documentElement.style.scrollBehavior = "smooth";
    return () => { document.documentElement.style.scrollBehavior = ""; };
  }, []);

  return (
    <div style={{ background: "var(--beige)" }} className="min-h-screen">
      <Header />
      <Hero />
      <Proposition />
      <Benefits />
      <Commitment />
      <Audience />
      <Preview />
      <About />
      <ApplicationCTA />
      <Footer />
      <PublicConsent />
    </div>
  );
};

export default PreLaunch;
